/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { setUserBookmarks as setUserBookmarksAction } from 'screens/Collection/Actions';
import Loader from 'components/Loader';
import Seo from 'componentsV2/Seo';
import { setRecommendationIdAction, setFreemiumModalVisibilityAction } from 'index/AppActions';
import BreadcrumbsComponent, { breadcrumbsChannelParser } from 'components/Breadcrumbs';
import FloatingStartFreeTrial from 'components/FloatingStartFreeTrial';
import {
  Events,
  handleAnalytics,
  PageNames,
  getTealiumRedesignEvents,
} from '@foxcorp/delta-web-analytics';
import Carousel from 'componentsV2/Carousel';
import { Hero, CHANNEL } from 'componentsV2/Hero';
import {
  clearChannelPanels,
  fetchChannels,
  updateVisibleElements as updateVisibleElementsAction,
} from './Actions';
import ChannelBanner from './components/ChannelBanner';
import ChannelPanel from './components/ChannelPanel';

import style from './Channel.module.scss';

const Channel = props => {
  const {
    accessToken,
    account,
    authSuccess,
    breakpoint,
    bookmarks,
    channelOrder,
    collectionImpressionCalled,
    coordinates,
    deviceId,
    disableScroll,
    favoritesManager,
    getUseUtagGlobal,
    hasAccountOrProvider,
    isAuthenticated,
    profileBookmarks,
    location,
    pageViewEvent,
    params: { channelId },
    preauthorizedResources,
    profileId,
    setLoading,
    setUserBookmarks,
  } = props;
  const {
    headline,
    headlineLogo,
    panels,
    seo,
    showcase,
    useShowcaseFallback,
    visibleCollections,
  } = useSelector(state => state.channel);
  const { startedPlayback: globalVideoStartedPlayback } = useSelector(state => state.globalVideo);
  const dispatch = useDispatch();
  const setRecommendationId = props => dispatch(setRecommendationIdAction(props));
  const updateVisibleElements = props => dispatch(updateVisibleElementsAction(props));
  const setFreemiumModalVisibility = props => dispatch(setFreemiumModalVisibilityAction(props));

  useEffect(() => {
    setUserBookmarks({ profileBookmarks });
  }, [setUserBookmarks, profileBookmarks]);

  useEffect(() => {
    pageViewEvent({
      pageName: PageNames.CHANNEL,
      pageTitle: channelId,
      useUtagGlobal: getUseUtagGlobal(),
    });
    handleAnalytics({
      event: Events.CHANNEL_PAGE_LANDING,
      payload: {
        pageType: 'channel',
        pageName: `channel:${channelId}`,
      },
    });
  }, []);

  useEffect(() => {
    if (!panels) {
      dispatch(fetchChannels(channelId, accessToken, deviceId));
    }
    return () => {
      dispatch(clearChannelPanels());
    };
  }, []);
  useEffect(() => {
    if (panels) {
      dispatch(fetchChannels(channelId, accessToken, deviceId));
    }
  }, [channelId]);

  if (!panels) return <Loader data-test="loader" setLoading={setLoading} center />;
  const panelItems = panels?.member;
  const items = showcase?.items?.member || [];
  const isOffset = items?.length > 1;
  const callCollectionImpressionEvent = () => {
    const tealiumRedesignEvents = getTealiumRedesignEvents({ account });
    if (tealiumRedesignEvents) {
      tealiumRedesignEvents.collectionImpression({ visibleCollections });
    }
    collectionImpressionCalled();
  };

  return (
    <>
      <ChannelBanner
        headline={headline}
        logo={headlineLogo}
        theme={style}
        breakpoint={breakpoint}
      />
      <Seo seo={seo} />
      <div className={style.carouselReworkOffset}>
        <Carousel type="showcase">
          {items.map((item, idx) => (
            <Hero
              accessToken={accessToken}
              bookmarks={bookmarks}
              callCollectionImpressionEvent={callCollectionImpressionEvent}
              idx={idx}
              isOffset={isOffset}
              item={item}
              panel={showcase}
              profileId={profileId}
              type={CHANNEL}
              key={item?.id}
            />
          ))}
        </Carousel>
      </div>
      <div className={style.channelContainer}>
        {panelItems?.map((panel, idx) => (
          <ChannelPanel
            accessToken={accessToken}
            account={account}
            authSuccess={authSuccess}
            breakpoint={breakpoint}
            channelOrder={channelOrder}
            collectionImpressionCalled={collectionImpressionCalled}
            coordinates={coordinates}
            deviceId={deviceId}
            disableScroll={disableScroll}
            favoritesManager={favoritesManager}
            globalVideoStartedPlayback={globalVideoStartedPlayback}
            headline={headline}
            id={idx}
            isAuthenticated={isAuthenticated}
            key={panel.id}
            location={location}
            panel={panel}
            panels={panels}
            preauthorizedResources={preauthorizedResources}
            setFreemiumModalVisibility={setFreemiumModalVisibility}
            setRecommendationId={setRecommendationId}
            showcase={showcase}
            updateVisibleElements={updateVisibleElements}
            useShowcaseFallback={useShowcaseFallback}
            visibleCollections={visibleCollections}
          />
        ))}
        <BreadcrumbsComponent
          data={breadcrumbsChannelParser(channelId)}
          customStyle={style.breadcrumbs}
        />
      </div>
      {!hasAccountOrProvider && <FloatingStartFreeTrial theme={style} />}
    </>
  );
};

export default connect(
  state => ({
    bookmarks: state.collection.bookmarks,
  }),
  {
    setUserBookmarks: setUserBookmarksAction,
  },
)(Channel);

Channel.fetchData = ({ store, params: { channelId }, accessToken, deviceId }) =>
  store.dispatch(dispatch => dispatch(fetchChannels(channelId, accessToken, deviceId)));

Channel.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  authSuccess: PropTypes.bool,
  breakpoint: PropTypes.string,
  bookmarks: PropTypes.shape({}),
  channelOrder: PropTypes.arrayOf(PropTypes.string),
  collectionImpressionCalled: PropTypes.func,
  coordinates: PropTypes.shape({}),
  deviceId: PropTypes.string,
  disableScroll: PropTypes.func,
  favoritesManager: PropTypes.shape({}),
  getUseUtagGlobal: PropTypes.func,
  hasAccountOrProvider: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  location: PropTypes.shape({}),
  pageViewEvent: PropTypes.func,
  params: PropTypes.shape({
    channelId: PropTypes.string,
  }).isRequired,
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  profileId: PropTypes.string,
  setLoading: PropTypes.func,
  profileBookmarks: PropTypes.shape({}),
  setUserBookmarks: PropTypes.func,
};
